import React, { type FC, type MouseEvent } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { alpha, Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import type { UUID } from '@/index'
import { standardTooltipProps } from '@/common/components'
import { formatTimestamp } from '@/common/utils'
import { getDisplaySessionName } from '@/features/events/conversation/state/types'
import { FIXED_TIMESTAMP_SIZE } from '@/features/events/conversation/shared'
import { EventType, resolveEventTypeColorFor } from '@/features/events/shared'
import type { BlockingInboxEntry } from '../types'
import { hasAllowOnceOption } from '../hooks'
import { formatErroredDescriptionLine } from '../utils'
import { getBubbleSx } from '../styles'

interface BlockingSessionCardProps {
  readonly entry: BlockingInboxEntry
  readonly isSubmitting: boolean
  readonly onSelect: (sessionId: UUID) => void
  readonly onAllowOnce: (entry: BlockingInboxEntry) => void
}

const BlockingSessionCardComponent: FC<BlockingSessionCardProps> = ({
  entry,
  isSubmitting,
  onSelect,
  onAllowOnce,
}) => {
  const theme = useTheme()
  const sessionName = getDisplaySessionName(entry.session)
  const accentColor = resolveEventTypeColorFor(entry.bucket, theme)
  const showQuickAllow = hasAllowOnceOption(entry)

  const handleSelect = (): void => {
    onSelect(entry.session.sessionId)
  }

  const handleAllowOnce = (event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation()
    event.preventDefault()
    onAllowOnce(entry)
  }

  return (
    <Box sx={cardContainerSx}>
      <Box sx={cardRowSx}>
        <Box
          component="button"
          type="button"
          onClick={handleSelect}
          sx={cardSx(theme, accentColor, showQuickAllow)}
          aria-label={`Open ${sessionName}: ${entry.intent}`}
        >
          <Typography variant="body2" sx={sessionNameSx} noWrap>
            {sessionName}
          </Typography>
          <BlockingIntent entry={entry} theme={theme} />
        </Box>
        {showQuickAllow && (
          <Tooltip {...standardTooltipProps} title="Allow once">
            <span style={quickAllowAnchorStyle}>
              <IconButton
                onClick={handleAllowOnce}
                disabled={isSubmitting}
                size="small"
                sx={quickAllowButtonSx(accentColor)}
                aria-label={`Allow once: ${sessionName}`}
              >
                <PlayArrowIcon sx={quickAllowIconSx} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={timestampSx}>
        {formatTimestamp(entry.event.timestamp)}
      </Typography>
    </Box>
  )
}

interface BlockingIntentProps {
  readonly entry: BlockingInboxEntry
  readonly theme: Theme
}

/**
 * Renders the intent as a vertical stack: the tool name on its own line (red)
 * above a description line (blue). Generic buckets (plan / questions /
 * context-less errored) have no tool name, so only the description line shows.
 *
 * The errored "— needs review" suffix lives solely in the flat `entry.intent`,
 * so it is re-appended to the split description line for errored entries to
 * avoid losing it.
 */
const BlockingIntent: FC<BlockingIntentProps> = ({ entry, theme }) => {
  if (!entry.toolName) {
    return (
      <Typography variant="caption" sx={intentDetailSx(theme)} noWrap component="span">
        {entry.intent}
      </Typography>
    )
  }

  const description =
    entry.bucket === EventType.ERRORED_EVENT
      ? formatErroredDescriptionLine(entry.detail)
      : (entry.detail ?? '')

  return (
    <>
      <Typography variant="caption" sx={toolNameSx(theme)} noWrap component="span">
        {entry.toolName}
      </Typography>
      {description && (
        <Typography variant="caption" sx={intentDetailSx(theme)} noWrap component="span">
          {description}
        </Typography>
      )}
    </>
  )
}

export const BlockingSessionCard = React.memo(BlockingSessionCardComponent)

// Right padding reserved on the open-trigger so its text never underlaps the
// absolutely positioned quick-allow control on the session-name row.
const QUICK_ALLOW_RESERVED_RIGHT = 5

const cardContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%',
}

// Positioning context for the quick-allow control, kept as a sibling of the
// open-trigger button so the two are never nested interactive elements.
const cardRowSx: SxProps<Theme> = {
  position: 'relative',
  width: '100%',
}

const cardSx = (theme: Theme, accentColor: string, hasQuickAllow: boolean): SxProps<Theme> => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  ...getBubbleSx('inner', theme),
  width: '100%',
  ...(hasQuickAllow ? { pr: QUICK_ALLOW_RESERVED_RIGHT } : {}),
  border: `1px solid ${alpha(accentColor, 0.3)}`,
  cursor: 'pointer',
  textAlign: 'left',
  font: 'inherit',
  color: 'inherit',
  transition: 'background-color 0.15s ease',
  '&:hover': {
    backgroundColor: 'action.hover',
  },
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: '-2px',
  },
})

const quickAllowAnchorStyle: React.CSSProperties = {
  position: 'absolute',
  top: 6,
  right: 8,
  display: 'inline-flex',
}

const quickAllowButtonSx = (accentColor: string): SxProps<Theme> => ({
  p: 0.5,
  color: accentColor,
  transition: 'transform 0.15s ease',
  '&:hover': {
    backgroundColor: 'transparent',
    transform: 'scale(1.2)',
  },
  '&.Mui-disabled': {
    color: alpha(accentColor, 0.4),
  },
})

const quickAllowIconSx: SxProps<Theme> = {
  fontSize: '1.5rem',
}

const lineSx: SxProps<Theme> = {
  display: 'block',
  alignSelf: 'stretch',
  maxWidth: '100%',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const sessionNameSx: SxProps<Theme> = {
  ...lineSx,
  fontSize: '0.95rem',
  fontWeight: 600,
  color: 'text.primary',
}

const toolNameSx = (theme: Theme): SxProps<Theme> => ({
  ...lineSx,
  color: theme.palette.mode === 'dark' ? theme.palette.error.light : theme.palette.error.main,
  fontWeight: 500,
})

const intentDetailSx = (theme: Theme): SxProps<Theme> => ({
  ...lineSx,
  fontSize: '0.8125rem',
  color: theme.palette.mode === 'dark' ? '#3aa9e0' : theme.palette.info.dark,
  fontWeight: 500,
})

const timestampSx: SxProps<Theme> = {
  display: 'block',
  mt: 0.5,
  textAlign: 'left',
  fontSize: FIXED_TIMESTAMP_SIZE,
}
